import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { useAuth } from '../../context/AuthContext'

// Deleting a lead or a deal is the superadmin's call. Sales raises a request;
// the row is removed only when that request is approved. See
// apps/api/src/routes/goodday/deletionRequests.ts.

export type CrmEntityType = 'lead' | 'deal'

export type CrmDeletionStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

export interface CrmDeletionRequest {
  id: string
  entityType: CrmEntityType
  entityId: string
  entityName: string
  entitySummary?: Record<string, any>
  reason: string
  status: CrmDeletionStatus
  requestedById?: string
  requestedByName?: string
  decidedByName?: string
  decisionNote?: string
  createdAt: string
  decidedAt?: string
  executedAt?: string
}

const KEY = ['crm-deletion-requests']

/**
 * Demo/synthetic rows in the CRM pages carry hand-made ids (`lead-3`, `d7`) and
 * have no database record behind them, so there is nothing to delete or ask
 * about. Only a real ObjectId qualifies.
 */
export function isDatabaseId(id?: string): boolean {
  return /^[a-f0-9]{24}$/i.test(String(id || ''))
}

interface ListFilters {
  status?: CrmDeletionStatus | 'all'
  entityType?: CrmEntityType
  entityId?: string
  enabled?: boolean
}

export function useDeletionRequests(filters: ListFilters = {}) {
  const { status, entityType, entityId, enabled = true } = filters
  return useQuery<CrmDeletionRequest[]>({
    queryKey: [...KEY, { status, entityType, entityId }],
    queryFn: async () => {
      const res = await api.get<any>('/deletion-requests', { status, entityType, entityId }, { skipErrorToast: true })
      return Array.isArray(res) ? res : (res?.data ?? [])
    },
    enabled,
    // A decision taken in another tab has to reach this one without a reload.
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  })
}

/** The open request for one record, if sales has already asked for it. */
export function usePendingDeletionRequest(entityType: CrmEntityType, entityId: string | undefined) {
  const query = useDeletionRequests({
    status: 'pending',
    entityType,
    entityId,
    enabled: !!entityId,
  })
  return { ...query, pending: query.data?.[0] ?? null }
}

/**
 * Delete if the caller is the superadmin, otherwise ask. The server makes the
 * same decision, so a stale client role can only ever produce a request, never
 * an unapproved delete.
 */
export function useDeleteCrmRecord() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const isSuperAdmin = isSuperAdminOrOwner(user?.role)

  return useMutation({
    mutationFn: async (input: { entityType: CrmEntityType; entityId: string; reason: string }) => {
      if (isSuperAdmin) {
        const res = await api.delete<any>(`/${input.entityType}s/${input.entityId}`, { skipErrorToast: true })
        return { deleted: true, message: res?.message } as const
      }
      const res = await api.post<any>('/deletion-requests', input, { skipErrorToast: true })
      return { deleted: !!res?.deleted, message: res?.message } as const
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['deals'] })
    },
  })
}

export function useDecideDeletionRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; action: 'approve' | 'reject'; note?: string }) => {
      const res = await api.post<any>(`/deletion-requests/${input.id}/${input.action}`, { note: input.note }, { skipErrorToast: true })
      return res
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

export function useCancelDeletionRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => api.post<any>(`/deletion-requests/${id}/cancel`, {}, { skipErrorToast: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}
